import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import { toast } from "@/v2-ui/toast"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import Button from "@/v2-backoffice-client/components/button"
import { useLicenseUpdateMutation } from "@/v2-backoffice-client/license/update/license.update.mutation"
import {
  evictLicenseQueries,
  dateInputToDatetime,
} from "@/v2-backoffice-client/license/license.utils"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseStartDateMenuItemProps = Omit<
  ActionMenuItemProps,
  "onClick"
> & {
  license: LicenseFragment
}

// Fixes a wrong start date on an operator-granted license — the one grant
// field that used to be settable at creation but never afterwards, forcing a
// delete-and-regrant that split the license's history in two. The temporal
// table records the correction like any other edit.
function LicenseStartDateMenuItem(props: LicenseStartDateMenuItemProps) {
  const { license, children, ...menuItemProps } = props
  const [isOpen, setIsOpen] = useState(false)
  const [startedAt, setStartedAt] = useState(
    license.StartedAt ? String(license.StartedAt).slice(0, 10) : "",
  )

  const [updateLicense, { loading, error }] = useLicenseUpdateMutation({
    update: evictLicenseQueries,
    onCompleted(data) {
      toast({
        variant: "success",
        title: "Start date updated",
        description: `${data.updateLicense.CustomerName ?? "The customer"}'s license now starts ${startedAt}.`,
      })
      setIsOpen(false)
    },
  })

  return (
    <>
      <ActionMenu.Item
        iconSrc="calendar-check"
        data-feature-code="UI_BackofficeLicense_Row_StartDate"
        onSelect={(e) => {
          e.preventDefault()
          setIsOpen(true)
        }}
        {...menuItemProps}
      >
        {children ?? "Edit Start Date"}
      </ActionMenu.Item>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Content align="top">
          <Dialog.Header>
            <Dialog.Title>Edit Start Date</Dialog.Title>
            <Dialog.Description>
              When {license.CustomerName ?? "this customer"}&apos;s license took
              effect. A correction, not a lifecycle action — extending a trial
              or ending the license live in their own menu items.
            </Dialog.Description>
          </Dialog.Header>

          <ErrorMessage className="my-4 text-center" error={error} />

          <div className="space-y-2">
            <Label htmlFor="licenseStartedAt">Started</Label>
            <Input
              id="licenseStartedAt"
              type="date"
              required
              value={startedAt}
              onChange={(e) => {
                setStartedAt(e.target.value)
              }}
            />
          </div>

          <Dialog.Footer className="mt-6 flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsOpen(false)
              }}
            >
              Cancel
            </Button>

            <Button
              type="button"
              isLoading={loading}
              disabled={!startedAt}
              data-feature-code="UI_BackofficeLicense_StartDate_Save"
              onClick={() => {
                updateLicense({
                  variables: {
                    id: license.Id,
                    input: { StartedAt: dateInputToDatetime(startedAt) },
                  },
                })
              }}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  )
}

export default LicenseStartDateMenuItem
